import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Package, Users } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import LeadGanttChart from "./LeadGanttChart";

interface Product {
  id: string;
  name: string;
  description: string | null;
  user_id: string;
  group_id: string | null;
}

interface AssociatedLead {
  id: string;
  lead_name: string;
}

interface ProductDetailsPanelProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProductDetailsPanel = ({ product, open, onOpenChange }: ProductDetailsPanelProps) => {
  const [associatedLeads, setAssociatedLeads] = useState<AssociatedLead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAssociatedLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data: leadProducts } = await supabase
        .from("lead_products")
        .select("lead_id")
        .eq("product_id", product.id);

      if (!leadProducts || leadProducts.length === 0) {
        setAssociatedLeads([]);
        setLoading(false);
        return;
      }

      const leadIds = leadProducts.map((lp: any) => lp.lead_id);
      const { data: leads } = await supabase
        .from("leads")
        .select("id, lead_name")
        .in("id", leadIds);

      setAssociatedLeads(leads || []);
    } catch (error) {
      console.error("Error fetching associated leads:", error);
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    if (open) {
      fetchAssociatedLeads();
    }
  }, [open, fetchAssociatedLeads]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            {product.name}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
            <p className="text-sm">
              {product.description || "No description provided."}
            </p>
          </div>

          <Separator />

          {/* Associated Leads */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Users className="h-4 w-4" />
              Associated Leads
              <Badge variant="secondary" className="ml-1 text-xs">
                {associatedLeads.length}
              </Badge>
            </h3>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : associatedLeads.length === 0 ? (
              <p className="text-sm text-muted-foreground">No leads associated with this product yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {associatedLeads.map((lead) => (
                  <Badge key={lead.id} variant="outline" className="text-xs">
                    {lead.lead_name}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Aggregated Gantt Chart */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Product Timeline (All Leads)
            </h3>
            {associatedLeads.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No leads to show timeline for.
              </p>
            ) : (
              <LeadGanttChart
                leadId={product.id}
                leadName={product.name}
                userId={product.user_id}
                groupId={product.group_id}
                aggregatedLeadIds={associatedLeads.map((l) => l.id)}
              />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ProductDetailsPanel;
